import { FINGER_NAMES, fingerFor, foldAccent, shiftFor, type Finger } from '@typing-racer/shared';
import { clear, h } from '../dom.js';

const ROWS = ['`1234567890-=', 'qwertyuiop[]\\', "asdfghjkl;'", 'zxcvbnm,./'];
const SHIFTED: Record<string, string> = {};
for (const [base, shifted] of [
  ['`1234567890-=', '~!@#$%^&*()_+'],
  ['[]\\', '{}|'],
  [";'", ':"'],
  [',./', '<>?'],
]) {
  for (let i = 0; i < base!.length; i++) SHIFTED[shifted![i]!] = base![i]!;
}

function baseKey(ch: string): string {
  if (SHIFTED[ch]) return SHIFTED[ch]!;
  return foldAccent(ch).toLowerCase();
}

/** Compact on-screen keyboard that colours keys by finger and highlights the next key. */
export class KeyboardView {
  readonly el: HTMLElement;
  private keys = new Map<string, HTMLElement>();
  private shiftL: HTMLElement;
  private shiftR: HTMLElement;
  private caption = h('div', { class: 'kb-caption' });
  private current: HTMLElement[] = [];

  constructor() {
    const rows = ROWS.map((row, r) =>
      h(
        'div',
        { class: 'kb-row' },
        r === 2 ? h('div', { class: 'key wide muted' }, 'caps') : null,
        [...row].map((k) => {
          const f = fingerFor(k) ?? 'LP';
          const el = h('div', { class: `key f-${f}`, 'data-key': k }, k === '\\' ? '\\' : k);
          this.keys.set(k, el);
          return el;
        }),
        r === 0 ? h('div', { class: 'key wide muted' }, '⌫') : null,
        r === 1 ? null : null,
      ),
    );
    this.shiftL = h('div', { class: 'key wider f-LP' }, 'shift');
    this.shiftR = h('div', { class: 'key wider f-RP' }, 'shift');
    rows[3]!.prepend(this.shiftL);
    rows[3]!.append(this.shiftR);
    const space = h('div', { class: 'key space f-T', 'data-key': ' ' }, 'space');
    this.keys.set(' ', space);
    rows.push(h('div', { class: 'kb-row' }, space));
    this.el = h('div', { class: 'keyboard' }, rows, this.caption);
  }

  /** Highlight the key for `ch` (or Backspace when `fix` is true). */
  setNext(ch: string | null, fix = false): void {
    for (const el of this.current) el.classList.remove('next');
    this.current = [];
    if (fix) {
      this.caption.textContent = 'Press Backspace to fix the mistake';
      const bs = this.el.querySelector('.key.muted:last-child') as HTMLElement | null;
      if (bs) {
        bs.classList.add('next');
        this.current.push(bs);
      }
      return;
    }
    if (!ch) {
      this.caption.textContent = '';
      return;
    }
    const key = this.keys.get(baseKey(ch));
    if (key) {
      key.classList.add('next');
      this.current.push(key);
    }
    const plain = foldAccent(ch);
    const shift = shiftFor(plain);
    if (shift) {
      const s = shift === 'L' ? this.shiftL : this.shiftR;
      s.classList.add('next');
      this.current.push(s);
    }
    const finger: Finger | null = fingerFor(plain);
    const name = finger ? FINGER_NAMES[finger] : 'a finger';
    const label = ch === ' ' ? 'Space' : plain !== ch ? `"${ch}" (type "${plain}")` : `"${ch}"`;
    this.caption.textContent = shift
      ? `${label}: ${name} with ${shift === 'L' ? 'left' : 'right'} Shift`
      : `${label}: ${name}`;
  }

  destroy(): void {
    clear(this.el);
    this.el.remove();
  }
}
